/**
 * IndexedDB 缓存错误类型定义
 * 
 * 提供统一的错误分类，便于错误处理和重试逻辑
 */

/**
 * 基础缓存错误类
 */
class CacheError extends Error {
    constructor(message, code) {
        super(message);
        this.name = this.constructor.name;
        this.code = code;
        Error.captureStackTrace(this, this.constructor);
    }
}

/**
 * 缓存记录未找到错误
 */
class CacheNotFoundError extends CacheError {
    constructor(message = 'Cache record not found') {
        super(message, 'CACHE_NOT_FOUND');
    }
}

/**
 * 事务错误
 */
class CacheTransactionError extends CacheError {
    constructor(message = 'Cache transaction failed', originalError = null) {
        super(message, 'CACHE_TRANSACTION_ERROR');
        this.originalError = originalError;
    }
}

/**
 * 配额超出错误
 */
class CacheQuotaExceededError extends CacheError {
    constructor(message = 'IndexedDB quota exceeded') {
        super(message, 'CACHE_QUOTA_EXCEEDED');
    }
}

/**
 * 并发冲突错误
 */
class CacheConcurrencyError extends CacheError {
    constructor(message = 'Cache concurrency conflict') {
        super(message, 'CACHE_CONCURRENCY_ERROR');
    }
}

/**
 * IndexedDB 不支持错误
 */
class CacheUnsupportedError extends CacheError {
    constructor(message = 'IndexedDB is not supported') {
        super(message, 'CACHE_UNSUPPORTED');
    }
}

/**
 * 数据库初始化错误
 */
class CacheInitError extends CacheError {
    constructor(message = 'Cache initialization failed', originalError = null) {
        super(message, 'CACHE_INIT_ERROR');
        this.originalError = originalError;
    }
}

/**
 * 判断错误是否可重试
 * @param {Error} error - 错误对象
 * @returns {boolean} 是否可重试
 */
function isRetryableError(error) {
    // 不可重试的错误
    if (
        error instanceof CacheNotFoundError ||
        error instanceof CacheQuotaExceededError ||
        error instanceof CacheUnsupportedError
    ) {
        return false;
    }

    // 事务错误和并发错误可以重试
    if (error instanceof CacheTransactionError || error instanceof CacheConcurrencyError) {
        return true;
    }

    // 检查原生 IndexedDB 错误
    if (error.name === 'QuotaExceededError') {
        return false;
    }

    // 默认可重试
    return true;
}

/**
 * 包装 IndexedDB 原生错误为自定义错误
 * @param {Error} error - 原生错误
 * @param {string} operation - 操作名称
 * @returns {CacheError} 包装后的错误
 */
function wrapIndexedDBError(error, operation = 'unknown operation') {
    if (!error) {
        return new CacheTransactionError(`${operation} failed: unknown error`);
    }

    const message = `${operation} failed: ${error.message || error.toString()}`;

    // 根据错误名称分类
    if (error.name === 'QuotaExceededError') {
        return new CacheQuotaExceededError(message);
    }

    if (error.name === 'NotFoundError') {
        return new CacheNotFoundError(message);
    }

    if (error.name === 'AbortError' || error.name === 'TransactionInactiveError') {
        return new CacheTransactionError(message, error);
    }

    // 默认为事务错误
    return new CacheTransactionError(message, error);
}

module.exports = {
    CacheError,
    CacheNotFoundError,
    CacheTransactionError,
    CacheQuotaExceededError,
    CacheConcurrencyError,
    CacheUnsupportedError,
    CacheInitError,
    isRetryableError,
    wrapIndexedDBError,
};

